import GPLink from '@/components/GPLink';
import { useGPLayoutContext } from '@/contexts/GPLayoutContext';

import { Avatar, Box, BoxProps, Stack, Typography } from '@mui/material';
import { GalleryAdd } from 'givepulse-iconsax-react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { UserResponse } from '../../../../requests/types/user';

type CoverProps = {
  userId: string;
  user: UserResponse;
};

const Cover = ({ userId, user }: CoverProps) => {
  const { navbar } = useGPLayoutContext();
  const viewerId = navbar.user?.id ?? 0;
  const isSelfViewing = parseInt(userId, 10) === viewerId;
  const { classes } = useStyles();
  const { t } = useTranslation();

  const sxCoverImageBox = {
    backgroundImage: `url(${user.cover_url ?? '/v1/images/default_cover.png'})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderRadius: '6px',
  } as const satisfies BoxProps['sx'];

  return (
    <>
      <Box
        className={classes.dimension}
        sx={sxCoverImageBox}
        role="img"
        aria-label={`Cover image for ${user.title}`}
      >
        {isSelfViewing && (
          <GPLink
            href={`/user/update/${viewerId}#settings`}
            color="textPrimary"
            underline="none"
            external={true}
          >
            <Box className="GPEditCoverOverlay">
              <GalleryAdd size={25} />
              <Typography component="p" variant="h5">
                {t('Change cover photo')}
              </Typography>
            </Box>
          </GPLink>
        )}
      </Box>
      <Box className={`${classes.coverUserInfo}`}>
        <Avatar
          alt={`${user.title} Avatar`}
          src={user.thumb_url}
          sx={{
            width: 100,
            height: 100,
            marginLeft: '10px',
            marginTop: '-50px',
            marginBottom: '10px',
            filter: 'drop-shadow(0px 4px 6px rgba(176, 188, 199, 0.4))',
          }}
        />
        <Stack
          sx={{
            marginLeft: 2,
            marginTop: 0.5
          }}>
          <Typography component="h1" variant="h4" sx={{
            fontSize: "1.5625rem"
          }}>
            {user.title}
          </Typography>
          <Typography
            variant="subtitle1"
            component="small"
            style={{ fontSize: 15, fontWeight: 350, marginTop: 3, display: 'block' }}
          >
            {[user.job_title, user.employer].filter(Boolean).join(' at ')}
          </Typography>
        </Stack>
      </Box>
    </>
  );
};

const useStyles = makeStyles()(theme => ({
  dimension: {
    position: 'relative',
    width: '100%',
    paddingTop: '17.5%',
    [theme.breakpoints.down('sm')]: {
      paddingTop: '25%',
    },
  },

  coverUserInfo: {
    display: 'flex',
    flexDirection: 'row',
  },
}));

export default Cover;
